import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, LockKeyhole } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] px-4 py-10 text-slate-100">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-6xl items-center justify-center">
        <div className="grid w-full gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <section className="fade-up">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.25em] text-blue-300">
              <ShieldCheck className="h-4 w-4" /> Secure Admin Import
            </div>
            <h1 className="font-headline text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              BusinessBox.co.in review engine for vendor catalogs.
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-8 text-slate-400">
              Upload vendor PDFs and CSVs, extract products with Gemini, enforce MOQ and pricing rules, then publish only customer-safe catalog data.
            </p>
          </section>
          <Card className="fade-up border-slate-800 bg-slate-900/90 text-slate-100 shadow-2xl" data-testid="admin-login-card">
            <CardHeader>
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-500/15 text-blue-300">
                <LockKeyhole className="h-6 w-6" />
              </div>
              <CardTitle className="font-headline text-2xl">Admin sign in</CardTitle>
              <CardDescription className="text-slate-400">Vendor costs and source files stay server-side.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    data-testid="admin-login-email-input"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    className="border-slate-700 bg-slate-950 text-slate-100"
                    placeholder="admin@businessbox.co.in"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    data-testid="admin-login-password-input"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className="border-slate-700 bg-slate-950 text-slate-100"
                    required
                  />
                </div>
                {error ? (
                  <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="admin-login-error">
                    {error}
                  </div>
                ) : null}
                <Button data-testid="admin-login-submit-button" type="submit" disabled={loading} className="w-full bg-blue-500 text-white hover:bg-blue-400">
                  {loading ? "Signing in..." : "Sign in securely"}
                </Button>
                <Link to="/" data-testid="login-catalog-link" className="block text-center text-sm text-blue-300 transition-colors hover:text-blue-200">
                  View customer catalog
                </Link>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
